import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  IconButton,
  Box,
  Slide,
  Portal
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

// Toast types
export type ToastType = 'error' | 'warning' | 'info' | 'success';

// Toast configuration interface
export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Context type
interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  hideToast: (id: string) => void;
}

// Create context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Provider component
export default function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      id,
      duration: 6000, // Default 6 seconds
      ...toast
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove after duration
    if (newToast.duration) {
      setTimeout(() => {
        hideToast(id);
      }, newToast.duration);
    }
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showError = useCallback((message: string, title?: string) => {
    showToast({ type: 'error', message, title, duration: 8000 });
  }, [showToast]);

  const showWarning = useCallback((message: string, title?: string) => {
    showToast({ type: 'warning', message, title, duration: 6000 });
  }, [showToast]);

  const showInfo = useCallback((message: string, title?: string) => {
    showToast({ type: 'info', message, title, duration: 5000 });
  }, [showToast]);

  const showSuccess = useCallback((message: string, title?: string) => {
    showToast({ type: 'success', message, title, duration: 4000 });
  }, [showToast]);

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showError,
        showWarning,
        showInfo,
        showSuccess,
        hideToast
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onClose={hideToast} />
    </ToastContext.Provider>
  );
}

// Hook to use toast
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast container component
function ToastContainer({
  toasts,
  onClose
}: {
  toasts: Toast[];
  onClose: (id: string) => void;
}) {
  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 1,
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
          minWidth: { sm: 400 },
          maxWidth: { sm: 600 }
        }}
      >
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onClose={() => onClose(toast.id)} />
        ))}
      </Box>
    </Portal>
  );
}

// Individual toast item
function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const [show, setShow] = useState(false);

  React.useEffect(() => {
    // Trigger animation
    setTimeout(() => setShow(true), 10);
  }, []);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  const getSeverity = () => {
    switch (toast.type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'success':
        return 'success';
      default:
        return 'info';
    }
  };

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Alert
        severity={getSeverity()}
        onClose={handleClose}
        sx={{
          width: '100%',
          boxShadow: 4,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {toast.action && (
              <IconButton
                size="small"
                onClick={() => {
                  toast.action?.onClick();
                  handleClose();
                }}
                sx={{ color: 'inherit' }}
              >
                {toast.action.label}
              </IconButton>
            )}
            <IconButton size="small" onClick={handleClose} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        {toast.title && <AlertTitle sx={{ fontWeight: 700 }}>{toast.title}</AlertTitle>}
        {toast.message}
      </Alert>
    </Slide>
  );
}

// Example usage component
export function ToastExample() {
  const toast = useToast();

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Click the buttons below to test different toast notifications
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <button
            onClick={() => toast.showError('Invalid username or password. Please try again.')}
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Login Error
          </button>

          <button
            onClick={() =>
              toast.showError(
                'You do not have permission to perform this action. Please contact your administrator.',
                'Permission Denied'
              )
            }
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Permission Error
          </button>

          <button
            onClick={() =>
              toast.showError(
                'Unable to connect to the server. Please check your internet connection.',
                'Connection Error'
              )
            }
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Network Error
          </button>

          <button
            onClick={() =>
              toast.showWarning(
                'This shift is already assigned to another employee.',
                'Scheduling Conflict'
              )
            }
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Warning
          </button>

          <button
            onClick={() =>
              toast.showInfo('Your changes have been saved as a draft.', 'Auto-saved')
            }
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Info
          </button>

          <button
            onClick={() =>
              toast.showSuccess('Employee successfully added to the schedule.', 'Success!')
            }
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Success
          </button>

          <button
            onClick={() =>
              toast.showToast({
                type: 'error',
                title: 'Shift Conflict',
                message: 'This employee is already scheduled for another shift at this time.',
                action: {
                  label: 'View Schedule',
                  onClick: () => console.log('Navigate to schedule')
                }
              })
            }
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Error with Action
          </button>

          <button
            onClick={() => {
              // Simulate multiple errors
              toast.showError('Failed to save changes to shift A');
              setTimeout(() => toast.showError('Failed to save changes to shift B'), 500);
              setTimeout(() => toast.showError('Failed to save changes to shift C'), 1000);
            }}
            style={{ padding: '12px 24px', cursor: 'pointer' }}
          >
            Multiple Errors
          </button>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <h3>Common Use Cases:</h3>
          <ul>
            <li>Login errors (invalid credentials, locked account)</li>
            <li>Permission denied errors</li>
            <li>Validation errors (invalid email, required fields)</li>
            <li>Network/connection errors</li>
            <li>Scheduling conflicts</li>
            <li>Success confirmations</li>
            <li>Warning messages</li>
            <li>Info notifications</li>
          </ul>
        </Box>
      </Box>
    </Box>
  );
}

// Predefined error messages for common scenarios
export const ErrorMessages = {
  // Authentication
  INVALID_CREDENTIALS: 'Invalid username or password. Please try again.',
  ACCOUNT_LOCKED: 'Your account has been locked. Please contact support.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  
  // Permissions
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  INSUFFICIENT_RIGHTS: 'Insufficient rights to access this resource.',
  
  // Network
  NETWORK_ERROR: 'Unable to connect to the server. Please check your connection.',
  TIMEOUT_ERROR: 'The request timed out. Please try again.',
  SERVER_ERROR: 'A server error occurred. Please try again later.',
  
  // Validation
  REQUIRED_FIELD: 'Please fill in all required fields.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  PASSWORD_MISMATCH: 'Passwords do not match.',
  
  // Scheduling
  SHIFT_CONFLICT: 'This employee is already scheduled for another shift at this time.',
  SCHEDULE_FULL: 'This shift is already fully staffed.',
  INVALID_TIME: 'End time must be after start time.',
  
  // General
  SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.',
  CHANGES_NOT_SAVED: 'Your changes could not be saved. Please try again.',
  RESOURCE_NOT_FOUND: 'The requested resource was not found.'
};